import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { AtlasTemplateService } from '@secca/core/services/atlas/atlas-template.service';
import {
  FillOutTemplateLineItem,
  FirstContactTemplateQuestion,
  FirstContactTemplateContext,
  GetFirstContactTemplateContextRequest,
  SelfFilledTemplateLineItem
} from '@secca/shared/models/atlas/atlas-template.model';
import { firstValueFrom } from 'rxjs';
import { CaseService } from '@secca/core/services/case.service';
import { IncidentService } from '@secca/core/services/incident.service';
import { NoteService } from '@secca/core/services/note.service';
import { AtlasContextFieldSettings, AtlasTemplateContext } from '@secca/shared/models/atlas/atlas-chatbot.model';
import { formatAtlasRequestErrorMessage } from '@secca/shared/helpers/atlas/atlas-errorhandler-helper';
import { normalizeContextFieldSettings } from '@secca/shared/helpers/atlas/atlas-context-field-settings.helper';
import { AtlasCaseContextService } from '@secca/core/services/atlas/atlas-case-context.service';

interface QuestionRow {
  questionText: string;
  answerText: string;
  answered: boolean;
  answerSource?: 'GPT' | 'SELF_FILLED';
  answerFieldSource?: string;
  manuallyEdited: boolean;
  templateName: string;
  originalIndex: number;
}

interface TemplateContextLoadResult {
  response: FirstContactTemplateContext;
  createdFromLookupParameters: boolean;
}

interface CauseLevelCodeSet {
  causeLevel1Code: string | null;
  causeLevel2Code: string | null;
  causeLevel3Code: string | null;
}

@Component({
  selector: 'app-atlas-chatbot-first-contact-template-dialog',
  templateUrl: './atlas-chatbot-first-contact-template-dialog.component.html',
  styleUrls: ['./atlas-chatbot-first-contact-template-dialog.component.scss'],
  standalone: false
})
export class AtlasChatbotFirstContactTemplateDialogComponent implements OnInit {
  private readonly atlasContextFieldSettingsKey = 'atlas-chat-context-field-settings';
  private templateContextLoadSeq = 0;
  private manualAnswerSaveSeq = 0;

  context: AtlasTemplateContext = {
    caseId: null,
    caseNumber: null,
    incidentCauseLevel1Code: null,
    incidentCauseLevel2Code: null,
    incidentCauseLevel3Code: null,
    customerProfileId: null,
    isWintherSportEnabled: null
  };
  loading = false;
  loadingMessage = 'Working on retrieving template context...';
  questionRows: QuestionRow[] = [];
  requestError: string | null = null;
  fillOutLoading = false;
  fillOutError: string | null = null;
  copied = false;
  pastedToPhoneNote = false;

  constructor(
    private atlasTemplateService: AtlasTemplateService,
    private caseService: CaseService,
    private incidentService: IncidentService,
    private atlasCaseContextService: AtlasCaseContextService,
    private noteService: NoteService,
    private dialogRef: MatDialogRef<AtlasChatbotFirstContactTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AtlasTemplateContext,
    public dialogBoundryService: DialogBoundryService
  ) {}

  ngOnInit(): void {
    this.applyContext(this.data);
  }

  applyContext(context: AtlasTemplateContext | null): void {
    if (!context) {
      this.requestError = 'No context found.';
      this.questionRows = [];
      this.loading = false;
      return;
    }

    this.context = context;
    this.requestError = null;
    this.fillOutError = null;
    this.copied = false;
    this.pastedToPhoneNote = false;
    this.loadTemplateContext();
  }

  close(): void {
    this.dialogRef.close();
  }

  async fillOutTemplate(): Promise<void> {
    if (!this.context.caseId || !this.context.caseNumber) {
      this.fillOutError = 'No case selected.';
      return;
    }

    const templateQuestions = this.questionRows.map(row => row.questionText).filter(Boolean);
    if (templateQuestions.length === 0) {
      this.fillOutError = 'No questions available to fill out.';
      return;
    }

    this.fillOutLoading = true;
    this.fillOutError = null;

    try {
      const caseData = await this.atlasCaseContextService.buildAtlasTemplateCaseData(
        this.context.caseId,
        this.getContextFieldSettings()
      );
      const response = await firstValueFrom(this.atlasTemplateService.fillOutTemplateUsingCaseData(
        this.context.caseNumber,
        {
          template: this.getTemplateQuestionsText(),
          caseData,
          selfFilledLineItems: this.getSelfFilledLineItems()
        }
      ));
      const lineItems = Array.isArray(response?.line_items) ? response.line_items : [];
      this.applyAnswersFromLineItems(lineItems);
    } catch (error) {
      this.fillOutError = this.getRequestErrorMessage(error, 'Unable to fill out template.');
    } finally {
      this.fillOutLoading = false;
    }
  }

  private async saveManualAnswers(): Promise<void> {
    if (!this.context.caseId || !this.context.caseNumber || this.questionRows.length === 0) {
      return;
    }

    const saveSeq = ++this.manualAnswerSaveSeq;
    this.fillOutError = null;

    try {
      const caseData = await this.atlasCaseContextService.buildAtlasTemplateCaseData(
        this.context.caseId,
        this.getContextFieldSettings()
      );
      const response = await firstValueFrom(this.atlasTemplateService.fillOutTemplateUsingCaseData(
        this.context.caseNumber,
        {
          template: this.getTemplateQuestionsText(),
          caseData,
          selfFilledLineItems: this.getSelfFilledLineItems(),
          skipGptFillOut: true
        }
      ));

      if (saveSeq !== this.manualAnswerSaveSeq) {
        return;
      }

      const lineItems = Array.isArray(response?.line_items) ? response.line_items : [];
      this.applyAnswersFromLineItems(lineItems);
    } catch (error) {
      if (saveSeq === this.manualAnswerSaveSeq) {
        this.fillOutError = this.getRequestErrorMessage(error, 'Unable to save manual answer.');
      }
    }
  }

  reloadTemplateContext(): void {
    this.loadingMessage = 'Working on retrieving template context...';
    this.loadTemplateContext(true);
  }

  showAnsweredSectionDivider(index: number): boolean {
    if (index <= 0 || index >= this.questionRows.length) {
      return false;
    }

    const currentRowAnswered = this.questionRows[index]?.answered;
    const previousRowAnswered = this.questionRows[index - 1]?.answered;
    return currentRowAnswered && !previousRowAnswered;
  }

  private async loadTemplateContext(forceReload = false): Promise<void> {
    const loadSeq = ++this.templateContextLoadSeq;
    const request = this.buildRequest(forceReload);
    if (!request) {
      this.requestError = 'Missing required context values.';
      return;
    }

    this.loading = true;
    this.requestError = null;
    this.questionRows = [];

    try {
      const result = await this.getTemplateContext(request, !forceReload);
      if (loadSeq !== this.templateContextLoadSeq) {
        return;
      }

      this.applyTemplateContext(result.response);
      await this.applyLatestFillOutTemplate(loadSeq);
    } catch (error) {
      if (loadSeq !== this.templateContextLoadSeq) {
        return;
      }
      this.requestError = this.getRequestErrorMessage(error, 'Unable to retrieve template context.');
    } finally {
      if (loadSeq === this.templateContextLoadSeq) {
        this.loading = false;
      }
    }
  }

  private async getTemplateContext(
    request: GetFirstContactTemplateContextRequest,
    fallbackToParsedRequest: boolean
  ): Promise<TemplateContextLoadResult> {
    try {
      const response = await firstValueFrom(this.atlasTemplateService.getFirstContactTemplateContext(request));
      const currentCaseCauseLevelCodes = fallbackToParsedRequest
        ? await this.getCurrentCaseCauseLevelCodes()
        : null;

      if (fallbackToParsedRequest && this.hasMismatchedCauseLevels(response, currentCaseCauseLevelCodes)) {
        this.applyCurrentCaseCauseLevelCodes(currentCaseCauseLevelCodes);
        return {
          response: await this.getParsedTemplateContext(),
          createdFromLookupParameters: true
        };
      }

      if (fallbackToParsedRequest && !this.hasTemplateQuestions(response)) {
        return {
          response: await this.getParsedTemplateContext(),
          createdFromLookupParameters: true
        };
      }
      return {
        response,
        createdFromLookupParameters: !fallbackToParsedRequest
      };
    } catch (error) {
      if (fallbackToParsedRequest && this.isPersistedTemplateRetrievalNotFound(error)) {
        return {
          response: await this.getParsedTemplateContext(),
          createdFromLookupParameters: true
        };
      }
      throw error;
    }
  }

  private async getCurrentCaseCauseLevelCodes(): Promise<CauseLevelCodeSet | null> {
    if (!this.context.caseId) {
      return null;
    }

    try {
      const activeCase = await firstValueFrom(this.caseService.getCase(this.context.caseId));
      if (!activeCase?.incident?.id) {
        return null;
      }

      const incident = await firstValueFrom(this.incidentService.getCaseIncident(activeCase.incident.id));
      return {
        causeLevel1Code: incident?.causeLevel1Code || null,
        causeLevel2Code: incident?.causeLevel2Code || null,
        causeLevel3Code: incident?.causeLevel3Code || null
      };
    } catch {
      return null;
    }
  }

  private hasMismatchedCauseLevels(
    response: FirstContactTemplateContext | null,
    currentCaseCauseLevelCodes: CauseLevelCodeSet | null
  ): boolean {
    if (!response?.cause_level_codes || !currentCaseCauseLevelCodes) {
      return false;
    }

    return this.normalizeCauseLevelCode(response.cause_level_codes.cause_level_1_code) !== this.normalizeCauseLevelCode(currentCaseCauseLevelCodes.causeLevel1Code) ||
      this.normalizeCauseLevelCode(response.cause_level_codes.cause_level_2_code) !== this.normalizeCauseLevelCode(currentCaseCauseLevelCodes.causeLevel2Code) ||
      this.normalizeCauseLevelCode(response.cause_level_codes.cause_level_3_code) !== this.normalizeCauseLevelCode(currentCaseCauseLevelCodes.causeLevel3Code);
  }

  private applyCurrentCaseCauseLevelCodes(currentCaseCauseLevelCodes: CauseLevelCodeSet | null): void {
    if (!currentCaseCauseLevelCodes) {
      return;
    }

    this.context = {
      ...this.context,
      incidentCauseLevel1Code: currentCaseCauseLevelCodes.causeLevel1Code,
      incidentCauseLevel2Code: currentCaseCauseLevelCodes.causeLevel2Code,
      incidentCauseLevel3Code: currentCaseCauseLevelCodes.causeLevel3Code
    };
  }

  private normalizeCauseLevelCode(causeLevelCode: string | null | undefined): string {
    const normalizedCauseLevelCode = String(causeLevelCode || '').trim().toLowerCase();
    return normalizedCauseLevelCode === '-' ? '' : normalizedCauseLevelCode;
  }

  private async getParsedTemplateContext(): Promise<FirstContactTemplateContext> {
    const parsedRequest = this.buildRequest(true);
    if (!parsedRequest) {
      throw new Error('Missing required context values.');
    }

    return await firstValueFrom(this.atlasTemplateService.getFirstContactTemplateContext(parsedRequest));
  }

  private applyTemplateContext(response: FirstContactTemplateContext): void {
    const questions = (response?.template_questions || [])
      .map(question => this.normalizeTemplateQuestion(question, response?.cause_level_template_mapping_name))
      .filter((question): question is FirstContactTemplateQuestion => !!question?.question_text);
    this.questionRows = this.mapQuestions(questions);
  }

  private normalizeTemplateQuestion(
    question: FirstContactTemplateQuestion | string,
    fallbackTemplateName: string | null | undefined
  ): FirstContactTemplateQuestion | null {
    if (typeof question === 'string') {
      return {
        question_text: question,
        template_name: fallbackTemplateName || ''
      };
    }

    return {
      question_text: question?.question_text || '',
      template_name: question?.template_name || fallbackTemplateName || ''
    };
  }

  private async applyLatestFillOutTemplate(loadSeq: number): Promise<void> {
    if (!this.context.caseNumber) {
      return;
    }

    try {
      const response = await firstValueFrom(this.atlasTemplateService.getLatestFillOutTemplateForCase(this.context.caseNumber));
      if (loadSeq !== this.templateContextLoadSeq) {
        return;
      }

      const lineItems = Array.isArray(response?.line_items) ? response.line_items : [];
      this.applyAnswersFromLineItems(lineItems);
    } catch (error) {
      if (!this.isPersistedTemplateRetrievalNotFound(error)) {
        throw error;
      }
    }
  }

  private hasTemplateQuestions(response: FirstContactTemplateContext): boolean {
    return Array.isArray(response?.template_questions) && response.template_questions.length > 0;
  }

  private isPersistedTemplateRetrievalNotFound(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private getRequestErrorMessage(error: unknown, fallbackMessage: string): string {
    return formatAtlasRequestErrorMessage(error, { fallbackMessage });
  }

  private buildRequest(includeLookupParameters: boolean): GetFirstContactTemplateContextRequest | null {
    if (!this.context.caseNumber) {
      return null;
    }

    if (!includeLookupParameters) {
      return {
        case_number: this.context.caseNumber
      };
    }

    if (!this.context.customerProfileId ||
      !this.context.incidentCauseLevel1Code && !this.context.incidentCauseLevel2Code) {
      return null;
    }

    return {
      case_number: this.context.caseNumber,
      customer_profile_id: Number(this.context.customerProfileId),
      cause_level_1_code: this.context.incidentCauseLevel1Code || undefined,
      cause_level_2_code: this.context.incidentCauseLevel2Code || undefined,
      cause_level_3_code: this.context.incidentCauseLevel3Code || undefined,
      winter_sport_enabled: this.context.isWintherSportEnabled == null ? undefined : this.context.isWintherSportEnabled,
      winther_sport_enabled: this.context.isWintherSportEnabled == null ? undefined : this.context.isWintherSportEnabled
    };
  }

  private mapQuestions(questions: FirstContactTemplateQuestion[]): QuestionRow[] {
    return questions.map((question, index) => ({
      questionText: question.question_text,
      answerText: '',
      answered: false,
      answerSource: undefined,
      answerFieldSource: undefined,
      manuallyEdited: false,
      templateName: question.template_name || '',
      originalIndex: index
    }));
  }

  private applyAnswersFromLineItems(lineItems: FillOutTemplateLineItem[]): void {
    const answerMap = new Map<string, string>();
    const answerSourceMap = new Map<string, 'GPT' | 'SELF_FILLED' | undefined>();
    const answerFieldSourceMap = new Map<string, string | undefined>();
    lineItems.forEach(item => {
      const normalizedQuestion = this.normalizeQuestion(item?.question || '');
      if (normalizedQuestion && item?.answer != null) {
        answerMap.set(normalizedQuestion, item.answer);
        answerSourceMap.set(normalizedQuestion, item.answer_source);
        answerFieldSourceMap.set(normalizedQuestion, item.field_where_information_is_found);
      }
    });

    this.questionRows = this.questionRows.map(row => {
      const normalizedQuestion = this.normalizeQuestion(row.questionText);
      const hasMatchedAnswer = answerMap.has(normalizedQuestion);
      const matchedAnswer = hasMatchedAnswer ? answerMap.get(normalizedQuestion) || '' : row.answerText;
      return {
        ...row,
        answerText: matchedAnswer,
        answered: this.hasAnswer(matchedAnswer),
        answerSource: hasMatchedAnswer ? answerSourceMap.get(normalizedQuestion) || 'GPT' : row.answerSource,
        answerFieldSource: hasMatchedAnswer ? answerFieldSourceMap.get(normalizedQuestion) : row.answerFieldSource,
        manuallyEdited: hasMatchedAnswer ? false : row.manuallyEdited
      };
    });
    this.sortQuestionRowsByAnswerStatus();
  }

  private getSelfFilledLineItems(): SelfFilledTemplateLineItem[] {
    return this.questionRows
      .filter(row => row.answerSource === 'SELF_FILLED')
      .sort((first, second) => first.originalIndex - second.originalIndex)
      .map(row => ({
        question: row.questionText,
        answer: row.answerText.trim()
      }));
  }

  private sortQuestionRowsByAnswerStatus(): void {
    this.questionRows = this.questionRows
      .sort((first, second) => {
        const firstAnswered = first.answered;
        const secondAnswered = second.answered;
        if (firstAnswered === secondAnswered) {
          return first.originalIndex - second.originalIndex;
        }
        return firstAnswered ? 1 : -1;
      });
  }

  private hasAnswer(value: string): boolean {
    return !!(value && value.trim());
  }

  private normalizeQuestion(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/^\s*\d+[\).\-\s]*/, '')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  onAnswerChanged(row: QuestionRow): void {
    row.manuallyEdited = true;
  }

  onAnswerBlur(row: QuestionRow): void {
    row.answered = this.hasAnswer(row.answerText);
    if (row.manuallyEdited) {
      row.answerSource = 'SELF_FILLED';
      row.answerFieldSource = 'manual';
    }
    this.sortQuestionRowsByAnswerStatus();
    if (row.manuallyEdited) {
      void this.saveManualAnswers();
    }
  }

  getAnswerSourceLabel(row: QuestionRow): string | null {
    if (!row.answered) {
      return null;
    }

    if (row.answerSource === 'SELF_FILLED') {
      return 'Manual';
    }

    return row.answerFieldSource ? `From ${row.answerFieldSource}` : 'From AI';
  }

  async copyQuestionsAndAnswers(): Promise<void> {
    const textToCopy = this.buildQuestionAnswerText();

    try {
      await navigator.clipboard.writeText(textToCopy);
      this.showCopiedState();
    } catch {
      this.fallbackCopy(textToCopy);
      this.showCopiedState();
    }
  }

  pasteToPhoneNoteField(): void {
    const textToPaste = this.buildQuestionAnswerText();
    if (!textToPaste.trim()) {
      return;
    }

    this.noteService.pasteToPhoneNote(this.context.caseId, textToPaste);
    this.showPastedToPhoneNoteState();
  }

  private getQuestionRowsInBackendOrder(): QuestionRow[] {
    return this.questionRows
      .slice()
      .sort((first, second) => first.originalIndex - second.originalIndex);
  }

  private buildQuestionAnswerText(): string {
    return this.getQuestionRowsInBackendOrder()
      .map(row => {
        const question = row.questionText.trim();
        const answer = row.answerText.trim();
        return answer ? `${question} - ${answer}` : question;
      })
      .join('\n');
  }

  private getTemplateQuestionsText(): string {
    return this.getQuestionRowsInBackendOrder()
      .map(row => row.questionText)
      .filter(Boolean)
      .join('\n');
  }

  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  private showCopiedState(): void {
    this.copied = true;
    window.setTimeout(() => {
      this.copied = false;
    }, 1800);
  }

  private showPastedToPhoneNoteState(): void {
    this.pastedToPhoneNote = true;
    window.setTimeout(() => {
      this.pastedToPhoneNote = false;
    }, 1800);
  }

  private getContextFieldSettings(): AtlasContextFieldSettings {
    const raw = sessionStorage.getItem(this.atlasContextFieldSettingsKey);
    if (!raw) {
      return normalizeContextFieldSettings(null);
    }

    try {
      return normalizeContextFieldSettings(JSON.parse(raw) as Partial<AtlasContextFieldSettings>);
    } catch {
      return normalizeContextFieldSettings(null);
    }
  }
}
